import omit from "lodash/omit";

import type { FilterUsage } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import { ALL_FILTER_USAGES } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";

import { leadsFilterRegistry } from "../registry";
import type { ILeadsSearchFiltersDto } from "../types/leadsSearchFilters.dto";

/**
 * Drops persisted values whose filter does not permit `usage`. A surface that cannot author a key must not
 * keep re-saving one it inherited: `leadsFilterPanelValueToDto` is total over every registered key, so a
 * value written before the filter opted out survives the panel round-trip untouched.
 *
 * Each key is removed from the category its definition declares (`apiCategory`) rather than from an assumed
 * container — registry keys are spread across `leadsFilters`, `contactsFilters` and `appsFilters`. The bare
 * root-level key is removed too, for rows written before the static api-category table stopped hoisting
 * unregistered keys.
 *
 * Keys outside the leads registry — CRM custom filters, signals, dynamic `crm:*` fields — are never touched.
 */
export const omitFiltersDeniedForUsage = (
    filters: Partial<ILeadsSearchFiltersDto>,
    usage: FilterUsage,
): Partial<ILeadsSearchFiltersDto> => {
    const denied = leadsFilterRegistry.filter(
        (definition) => !(definition.usages ?? ALL_FILTER_USAGES).includes(usage),
    );

    return denied.reduce<Partial<ILeadsSearchFiltersDto>>((result, { key, apiCategory }) => {
        const category = result[apiCategory];
        const isInCategory = category != null && key in category;
        const isAtRoot = key in result;

        if (!isInCategory && !isAtRoot) {
            return result;
        }

        const withoutLegacyRootKey = isAtRoot ? (omit(result, key) as Partial<ILeadsSearchFiltersDto>) : result;

        return isInCategory ? { ...withoutLegacyRootKey, [apiCategory]: omit(category, key) } : withoutLegacyRootKey;
    }, filters);
};
